import { FOR_PURPOSE_LEVERS, type LeverDef } from "./config.js";
import type { Block, BlockType, Goal } from "./schema.js";
import { newId as defaultNewId, type IdGenerator } from "./ids.js";

/** A new, empty goal (reference `blankGoal`). */
export function blankGoal(newId: IdGenerator = defaultNewId): Goal {
  return {
    id: newId("g"),
    title: "",
    stakeholder: "",
    personalWin: "",
    rightNow: "",
    objective: "",
    capability: "",
    outcome: "",
    costInaction: "",
    levers: {
      fund: { on: false, measure: "" },
      eff: { on: false, measure: "" },
      exp: { on: false, measure: "" },
      risk: { on: false, measure: "" },
    },
    custom: [],
    vcase: { blocks: [] },
    complexity: { scale: 3, skills: 3, dependencies: 3, change: 3, custom: [], dependsOn: "" },
  };
}

/** Default label for each block type (reference `addBlock`). */
export const BLOCK_LABELS: Readonly<Record<BlockType, string>> = {
  revenue: "Revenue uplift",
  efficiency: "Time saved",
  cost: "Cost or risk avoided",
};

/** Badge text for each block type in the Quantify view. */
export const BLOCK_BADGES: Readonly<Record<BlockType, string>> = {
  revenue: "Revenue",
  efficiency: "Efficiency",
  cost: "Cost / risk",
};

/** A new block with the reference defaults (reference `addBlock`). */
export function newBlock(type: BlockType, newId: IdGenerator = defaultNewId): Block {
  const id = newId("b");
  const label = BLOCK_LABELS[type];
  switch (type) {
    case "revenue":
      return {
        id,
        type,
        label,
        method: "percent",
        baseline: "",
        upliftPct: "",
        upliftPctLow: "",
        upliftPctHigh: "",
        donors: "",
        avgGift: "",
        extraDonors: "",
        avgGiftIncreasePct: "",
        ramp: 100,
        growthPct: 0,
      };
    case "efficiency":
      return {
        id,
        type,
        label,
        hoursPerWeekEach: "",
        staff: "",
        savingPct: 100,
        weeksPerYear: 48,
        hourlyCost: "",
        growthPct: 0,
      };
    case "cost":
      return { id, type, label, annualAmount: "", likelihoodPct: 100, growthPct: 0 };
  }
}

/** Display name of a goal: its trimmed title, or "Goal n" (reference `goalLabel`). */
export function goalLabel(g: { title?: string | null }, i: number): string {
  return g.title && g.title.trim() ? g.title.trim() : "Goal " + (i + 1);
}

export interface PickedLever {
  name: string;
  measure: string;
}

/**
 * The value areas a goal uses, in display order: switched-on levers, then named custom levers
 * (reference `pickedLevers`).
 */
export function pickedLevers(
  g: Pick<Goal, "levers" | "custom">,
  defs: readonly LeverDef[] = FOR_PURPOSE_LEVERS,
): PickedLever[] {
  const out: PickedLever[] = [];
  defs.forEach((L) => {
    const lv = g.levers[L.key];
    if (lv.on) out.push({ name: L.title, measure: lv.measure });
  });
  g.custom.forEach((c) => {
    if (c.name.trim()) out.push({ name: c.name, measure: c.measure });
  });
  return out;
}
